<script lang="ts" setup>
// PageSourceDonneesCompteur (DEV-1508 lot 2) — l'entrée « ombre » de la page
// « Données du compteur » d'un compteur Enedis.
//
// INTENTION (décision de Mathieu du 10/09/2026) : la nouvelle page doit être
// regardable en production, sur de vrais compteurs, AVANT d'être servie à qui
// que ce soit. Elle vit donc à une adresse à elle, que rien dans l'application
// ne donne : ni menu, ni onglet, ni bouton, ni redirection. On y arrive en
// collant l'URL, jamais en cliquant.
//
// Trois conséquences tenues par ce fichier :
//
//  • MÊMES GARDES que `/source/:_id_source/data`. L'adresse est discrète, elle
//    n'est pas ouverte : même authentification, même appartenance du bâtiment,
//    mêmes droits. Une page cachée sans garde serait une fuite, pas une ombre.
//
//  • ELLE EXISTE QUEL QUE SOIT L'INTERRUPTEUR. `ENEDIS_PAGE_DONNEES_V2` décide
//    de ce que rend la route STANDARD (lot 3) ; il ne décide pas de l'ombre,
//    sinon on ne pourrait pas regarder la page avant de l'armer.
//
//  • UNE AUTRE SOURCE N'A RIEN À VOIR ICI. Un PCE, un CPCU, un compteur d'eau
//    qui atterrirait sur cette adresse repart vers sa page de données. On ne
//    lui montre pas une page vide en lui laissant croire qu'il n'a pas de
//    donnée.
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useSourceStore } from "@core/stores/source.store"
import { useGraphStore } from "@modules/data/stores/graph.store"
import { useWorkflowEnedisMessage } from "@core/composables"
import { SOURCES_TYPES } from "@core/constants"
import { formatIdSource } from "@core/helpers/format.helper"
import SourceDataEnedisView from "@modules/data/components/data/enedis/SourceDataEnedisView.vue"
import type { SegmentEnedis } from "@modules/data/utils/ongletsEnedis"
import type { DirectionEnedis } from "@modules/data/services/sourceDataEnedis.service"
import {
    lireInformationsPdl,
    segmentDuPdl,
    directionsDuPdl,
    variablesServiesDuPdl,
} from "@modules/data/utils/informationsPdl"
import {
    contexteDeLaPageCompteur,
    JOURS_DU_CONTEXTE,
} from "@modules/data/utils/pageCompteurV2"

/** Le titre de la page, le même mot que la page de données standard d'un compteur. */
const TITRE = "Données du compteur"

const route = useRoute()
const router = useRouter()
const sourceStore = useSourceStore()
const graphStore = useGraphStore()

/**
 * LE VERDICT DU COMPTEUR. C'est lui, et lui seul, qui sait ce que ce compteur
 * DOIT (flux, attendu par flux, dernière mesure) : la ligne de fraîcheur et
 * l'état de chaque jour de la bande s'y lisent, ils ne se calculent pas ici
 * (engagement E4, aucun seuil de jours dans le front).
 *
 * Même composable et même route que la page de données standard : aucune
 * lecture nouvelle, aucun droit nouveau.
 */
const { fetch: chargerLeVerdict, workflowEnedisList } =
    useWorkflowEnedisMessage("source")

const verdict = computed(() => workflowEnedisList.value?.[0]?.verdict ?? null)

/**
 * Vrai dès que la lecture du verdict est TERMINÉE, qu'elle ait rendu un verdict
 * ou non.
 *
 * `verdict === null` ne suffit pas à le savoir : c'est aussi ce que vaut un
 * verdict pas encore lu. La bande de complétude a besoin de la différence, sans
 * quoi elle peint « Manquant » sur des jours dont le verdict dira, une seconde
 * plus tard, qu'ils sont en attente de collecte (décision du 11/09).
 */
const verdictRecu = ref(false)

/** Id de la source lu de la route (string, ou premier élément si tableau). */
const sourceIdParam = computed(() => {
    const p = route.params._id_source
    return Array.isArray(p) ? p[0] : p
})

const currentSource = computed(() => {
    const id = sourceIdParam.value
    return id ? sourceStore.getSourceById(id) : undefined
})

/** Vrai pour un compteur Enedis. `idtype` est le champ fiable : dans le store la
 *  source est fusionnée dapi + legacy et le `type` legacy, souvent null, écrase
 *  celui calculé par dapi. On teste les deux, `idtype` d'abord. */
const estCompteurEnedis = computed(() => {
    const s = currentSource.value
    return s?.idtype === SOURCES_TYPES.PDL || s?.type === SOURCES_TYPES.PDL
})

/** Informations du point, lues à leur place et non par le `type` legacy.
 *  Même lecture que la page de données standard : le piège de l'indexation par
 *  `type` et son remède vivent dans `utils/informationsPdl.ts`. */
const informationsPdl = computed(() => lireInformationsPdl(currentSource.value))

/** Segment du point. Absent se lit « je ne sais pas », jamais un segment par défaut. */
const segmentPdl = computed<SegmentEnedis>(() =>
    segmentDuPdl(informationsPdl.value)
)

/** Les sens que ce compteur porte RÉELLEMENT (E5) : voir `informationsPdl.ts`. */
const directions = computed<DirectionEnedis[]>(() =>
    directionsDuPdl(informationsPdl.value)
)

/**
 * Les variables réellement servies pour ce compteur, par sens. Ce sont elles qui
 * décident des onglets (E1) : la page n'en devine aucune.
 */
const variablesServies = computed(() =>
    variablesServiesDuPdl(informationsPdl.value)
)

/**
 * Le CONTEXTE de la page : les 365 derniers jours, bornés par la première donnée
 * détenue.
 *
 * Il ne se calcule pas ici : depuis le lot 3c, les DEUX entrées appellent la
 * même fonction (`contexteDeLaPageCompteur`). C'est la seule façon de tenir la
 * promesse « la route standard armée montre la même page que l'ombre » : deux
 * calculs jumeaux se seraient séparés au premier correctif porté d'un seul côté.
 * Le pourquoi de l'année et du plafond par le dû vit avec la fonction.
 */
/**
 * LA LARGEUR DU CONTEXTE, en jours : ce que le raccourci pressé demande.
 *
 * Elle vit ICI, avec le calcul du contexte, et pas dans la vue : c'est la même
 * fonction qui sait où le dû s'arrête pour les deux entrées (lot 5c, écart B3).
 */
const joursDuContexte = ref(JOURS_DU_CONTEXTE)

const contexte = computed(() =>
    // Les informations descendent AUSSI : la borne `data.<sens>.date_from` du
    // sens entre dans le calcul de la première donnée détenue (lot 5a, D4).
    contexteDeLaPageCompteur(
        variablesServies.value,
        undefined,
        informationsPdl.value,
        joursDuContexte.value
    )
)

const prm = computed(() =>
    currentSource.value?.idsource
        ? formatIdSource(currentSource.value.idsource, "pdl")
        : null
)

/** Une source qui n'est pas un compteur Enedis repart vers sa page de données. */
function renvoyerSiCeNestPasUnCompteurEnedis(): void {
    if (!currentSource.value) return
    if (estCompteurEnedis.value) return
    router.replace({
        name: "PageSourceData",
        params: { _id_source: String(sourceIdParam.value) },
    })
}

/**
 * Vrai dès que CETTE page a posé son identifiant dans le store partagé. Une
 * source renvoyée n'y a jamais touché : elle ne doit rien effacer en partant.
 */
let identifiantPose = false

onMounted(async () => {
    const id = sourceIdParam.value
    if (!id) return
    if (!currentSource.value) {
        await sourceStore.fetchSourceById({ _id_source: id })
    }
    renvoyerSiCeNestPasUnCompteurEnedis()
    if (!estCompteurEnedis.value) return
    // ⚠️ APRÈS `setCurrentSourceId` : le composable lit l'identifiant courant
    // dans le store graph ; un appel plus tôt partirait sur `null` (même piège
    // et même ordre que `PageSourceData.vue`).
    graphStore.setCurrentSourceId(id)
    identifiantPose = true
    // La lecture ne lève pas : elle se termine même en erreur, et c'est bien
    // « terminée » qu'on annonce, pas « réussie ».
    await chargerLeVerdict()
    verdictRecu.value = true
})

// `graphStore` est PARTAGÉ avec la page de données standard. Sans ces deux
// gestes, une réponse en retard retomberait dans la page suivante sous le
// mauvais compteur, et l'identifiant de celui-ci resterait courant après le
// départ. La page standard fait exactement la même chose (`PageSourceData.vue`).
onBeforeUnmount(() => {
    if (!identifiantPose) return
    graphStore.cancelPendingGraphRequests()
    graphStore.setCurrentSourceId(null)
})

watch(currentSource, renvoyerSiCeNestPasUnCompteurEnedis)
</script>

<template>
    <!-- Le même cadre que toute page de l'application (`.page` : largeur
         plafonnée, marges, colonne) et le même en-tête que la page de données
         standard (fil d'Ariane + titre). Sans lui, la vue s'écrivait à même le
         bord de la barre latérale (capture du 10/09). -->
    <div class="page page-source-data">
        <HeaderPageSource
            v-if="estCompteurEnedis && sourceIdParam"
            :title="TITRE"
            :source-id="String(sourceIdParam)"
        />
        <SourceDataEnedisView
            v-if="estCompteurEnedis"
            entree="shadow"
            :id-source="sourceIdParam ?? null"
            :prm="prm"
            :informations="informationsPdl"
            :segment="segmentPdl"
            :directions="directions"
            :variables-servies="variablesServies"
            :contexte="contexte"
            :jours-contexte="joursDuContexte"
            :verdict="verdict"
            :verdict-recu="verdictRecu"
            @update:jours-contexte="joursDuContexte = $event"
        />
    </div>
</template>
